// Mock authentication service for local development
// Simulates AWS Cognito behavior without AWS dependencies

export interface AuthResult {
  success: boolean;
  message?: string;
  session?: any;
}

export interface RegisterData {
  email: string;
  password: string;
}

export interface LoginData {
  email: string;
  password: string;
}

interface MockUser {
  userId: string;
  email: string;
  password: string;
  registrationDate: number;
  verified: boolean;
}

interface MockSession {
  token: string;
  email: string;
  userId: string;
  expiresAt: number;
}

const MOCK_USERS_KEY = 'mockUsers';
const MOCK_SESSION_KEY = 'mockSession';

// Helper to get all users
const getUsers = (): Record<string, MockUser> => {
  const users = localStorage.getItem(MOCK_USERS_KEY);
  return users ? JSON.parse(users) : {};
};

// Helper to save users
const saveUsers = (users: Record<string, MockUser>): void => {
  localStorage.setItem(MOCK_USERS_KEY, JSON.stringify(users));
};

// Helper to generate fake JWT token
const generateToken = (email: string, userId: string): string => {
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const payload = btoa(JSON.stringify({
    sub: userId,
    email,
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 3600, // 1 hour
  }));
  const signature = btoa(`mock-signature-${Date.now()}`);
  return `${header}.${payload}.${signature}`;
};

// Helper to create session
const createSession = (email: string, userId: string): MockSession => {
  const session: MockSession = {
    token: generateToken(email, userId),
    email,
    userId,
    expiresAt: Date.now() + 3600000, // 1 hour
  };
  localStorage.setItem(MOCK_SESSION_KEY, JSON.stringify(session));
  localStorage.setItem('authToken', session.token);
  localStorage.setItem('userEmail', email);
  return session;
};

// Simulate network delay
const delay = (ms: number = 300): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

// Register a new user
export const register = async (data: RegisterData): Promise<AuthResult> => {
  await delay();
  
  const users = getUsers();
  
  // Check if user already exists
  if (users[data.email]) {
    return {
      success: false,
      message: 'User already exists',
    };
  }
  
  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(data.email)) {
    return {
      success: false,
      message: 'Invalid email format',
    };
  }
  
  // Validate password strength
  if (data.password.length < 8) {
    return {
      success: false,
      message: 'Password must be at least 8 characters long',
    };
  }
  
  // Create new user
  const userId = `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const newUser: MockUser = {
    userId,
    email: data.email,
    password: data.password, // In real app, this would be hashed
    registrationDate: Date.now(),
    verified: true, // Auto-verify in mock mode for testing
  };
  
  users[data.email] = newUser;
  saveUsers(users);
  
  // In mock mode, auto-login for easier testing
  // In production, user must wait for admin approval
  createSession(data.email, userId);
  
  return {
    success: true,
    message: 'Registration successful! (Mock mode: auto-approved and logged in)',
  };
};

// Login user
export const login = async (data: LoginData): Promise<AuthResult> => {
  await delay();
  
  const users = getUsers();
  const user = users[data.email];
  
  if (!user) {
    return {
      success: false,
      message: 'User not found. Please register first.',
    };
  }
  
  if (user.password !== data.password) {
    return {
      success: false,
      message: 'Invalid email or password',
    };
  }
  
  const session = createSession(data.email, user.userId);
  
  return {
    success: true,
    message: 'Login successful (mock mode)',
    session,
  };
};

// Logout user
export const logout = (): void => {
  localStorage.removeItem(MOCK_SESSION_KEY);
  localStorage.removeItem('authToken');
  localStorage.removeItem('userEmail');
};

// Get current user
export const getCurrentUser = (): any => {
  const sessionData = localStorage.getItem(MOCK_SESSION_KEY);
  if (!sessionData) return null;
  
  const session: MockSession = JSON.parse(sessionData);
  
  // Check if session is expired
  if (session.expiresAt < Date.now()) {
    logout();
    return null;
  }
  
  return {
    username: session.email,
    userId: session.userId,
  };
};

// Get auth token
export const getAuthToken = (): string | null => {
  const sessionData = localStorage.getItem(MOCK_SESSION_KEY);
  if (!sessionData) return null;
  
  const session: MockSession = JSON.parse(sessionData);
  
  // Check if session is expired
  if (session.expiresAt < Date.now()) {
    logout();
    return null;
  }
  
  return session.token;
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  const token = getAuthToken();
  return token !== null;
};

// Get current session
export const getCurrentSession = async (): Promise<any> => {
  const sessionData = localStorage.getItem(MOCK_SESSION_KEY);
  if (!sessionData) return null;
  
  const session: MockSession = JSON.parse(sessionData);
  
  // Check if session is expired
  if (session.expiresAt < Date.now()) {
    logout();
    return null;
  }
  
  return session;
};

// Verify email with code (mock - always succeeds)
export const verifyEmail = async (email: string, code: string): Promise<AuthResult> => {
  await delay();
  
  return {
    success: true,
    message: 'Email verified successfully (mock mode)',
  };
};

// Resend verification code (mock - always succeeds)
export const resendVerificationCode = async (email: string): Promise<AuthResult> => {
  await delay();
  
  return {
    success: true,
    message: 'Verification code sent (mock mode)',
  };
};

// Forgot password (mock - always succeeds)
export const forgotPassword = async (email: string): Promise<AuthResult> => {
  await delay();
  
  const users = getUsers();
  if (!users[email]) {
    return {
      success: false,
      message: 'User not found',
    };
  }
  
  return {
    success: true,
    message: 'Password reset code sent to your email (mock mode)',
  };
};

// Reset password with code (mock - always succeeds)
export const resetPassword = async (
  email: string,
  code: string,
  newPassword: string
): Promise<AuthResult> => {
  await delay();
  
  const users = getUsers();
  const user = users[email];
  
  if (!user) {
    return {
      success: false,
      message: 'User not found',
    };
  }
  
  if (newPassword.length < 8) {
    return {
      success: false,
      message: 'Password must be at least 8 characters long',
    };
  }
  
  // Update password
  user.password = newPassword;
  users[email] = user;
  saveUsers(users);
  
  return {
    success: true,
    message: 'Password reset successful (mock mode)',
  };
};

// Get user ID from current session
export const getUserId = (): string | null => {
  const sessionData = localStorage.getItem(MOCK_SESSION_KEY);
  if (!sessionData) return null;
  
  const session: MockSession = JSON.parse(sessionData);
  
  // Check if session is expired
  if (session.expiresAt < Date.now()) {
    logout();
    return null;
  }
  
  return session.userId;
};
